import { API, WS_URL } from '../api.js';
// ChatDrawer.jsx
// Real-time DM chat drawer. Opens as a right-side slide-in panel.
//
// Props:
//   targetUser  – { id, displayName, avatarEmoji, avatarUrl }
//   onClose     – () => void

import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Avatar from './Avatar';

const MIME_ICONS = {
  image: '🖼️',
  video: '🎬',
  file:  '📎',
};

function detectType(ext) {
  const e = ext?.toLowerCase();
  if (['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'].includes(e)) return 'image';
  if (['.mp4', '.webm', '.mov', '.avi', '.mkv'].includes(e))          return 'video';
  return 'file';
}

function timeStr(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function dateLabel(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString())     return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ChatDrawer({ targetUser, communityId, onClose }) {
  const { user, token } = useAuth();
  const [convId,    setConvId]    = useState(null);
  const [messages,  setMessages]  = useState([]);
  const [text,      setText]      = useState('');
  const [uploading, setUploading] = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [wsReady,   setWsReady]   = useState(false);
  const [error,     setError]     = useState(null);

  const [previewImage, setPreviewImage] = useState(null);

  const wsRef        = useRef(null);
  const bottomRef    = useRef(null);
  const fileInputRef = useRef(null);
  const reconnTimer  = useRef(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (previewImage) {
          setPreviewImage(null);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, previewImage]);

  // ── 1. Create / fetch conversation id ──────────────────────────────────────
  useEffect(() => {
    if (!targetUser?.id || !token) return;
    setError(null);
    setLoading(true);
    fetch(`${API}/api/chat/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ targetUserId: targetUser.id, communityId }),
    })
      .then(async r => {
        if (r.ok) return r.json();
        const err = await r.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to start conversation');
      })
      .then(data => {
        if (data?.id) setConvId(data.id);
        else setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [targetUser?.id, token, communityId]);

  // ── 2. Load message history once convId is known ───────────────────────────
  useEffect(() => {
    if (!convId || !token) return;
    setLoading(true);
    fetch(`${API}/api/chat/conversations/${convId}/messages`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : [])
      .then(data => { setMessages(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [convId, token]);

  // ── 3. WebSocket for real-time incoming messages ───────────────────────────
  const connectWS = useCallback(() => {
    if (!convId || !token) return;

    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      // Send init frame to subscribe to the chat channel
      ws.send(JSON.stringify({ type: 'chat', convId, token }));
      setWsReady(true);
    };

    ws.onmessage = (ev) => {
      try {
        const frame = JSON.parse(ev.data);
        if (frame.type === 'chat_message' && frame.payload) {
          const msg = typeof frame.payload === 'string'
            ? JSON.parse(frame.payload)
            : frame.payload;
          // Only add if not from me (we add ours optimistically)
          if (msg.senderId !== user?.id) {
            setMessages(prev => {
              const dup = prev.some(m => m.id === msg.id);
              return dup ? prev : [...prev, msg];
            });
          }
        }
      } catch (_) {}
    };

    ws.onclose = () => {
      setWsReady(false);
      reconnTimer.current = setTimeout(connectWS, 3000);
    };

    ws.onerror = () => ws.close();
  }, [convId, token, user?.id]);

  useEffect(() => {
    connectWS();
    return () => {
      clearTimeout(reconnTimer.current);
      wsRef.current?.close();
    };
  }, [connectWS]);

  // ── 4. Auto-scroll to bottom on new messages ───────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── 5. Send text message ───────────────────────────────────────────────────
  async function sendText(e) {
    e.preventDefault();
    const content = text.trim();
    if (!content || !convId) return;
    setText('');

    const optimistic = {
      id: `opt_${Date.now()}`,
      senderId: user?.id,
      senderName: user?.display_name || 'You',
      content,
      createdAt: new Date().toISOString(),
      _optimistic: true,
    };
    setMessages(prev => [...prev, optimistic]);

    try {
      const res = await fetch(`${API}/api/chat/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content }),
      });
      if (res.ok) {
        const saved = await res.json();
        setMessages(prev => prev.map(m => m.id === optimistic.id ? { ...saved, _optimistic: false } : m));
      }
    } catch (_) {}
  }

  // ── 6. Upload attachment ───────────────────────────────────────────────────
  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file || !convId) return;
    e.target.value = '';

    setUploading(true);
    const ext = '.' + (file.name.split('.').pop() || 'bin');
    const attachmentType = detectType(ext);
    const attachmentName = file.name;

    try {
      const uploadRes = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
        body: file,
      });
      if (!uploadRes.ok) throw new Error('Upload failed');
      const { url } = await uploadRes.json();

      const res = await fetch(`${API}/api/chat/conversations/${convId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ attachmentUrl: url, attachmentType, attachmentName }),
      });
      if (res.ok) {
        const saved = await res.json();
        setMessages(prev => [...prev, { ...saved, _optimistic: false }]);
      }
    } catch (_) {}
    setUploading(false);
  }

  // ── Helpers ────────────────────────────────────────────────────────────────
  const avatarLetter = (targetUser?.displayName || targetUser?.display_name || '?')[0]?.toUpperCase();
  const avatarUrl    = targetUser?.avatarUrl || targetUser?.avatar_url;
  console.log('ChatDrawer targetUser debug:', targetUser);
  console.log('ChatDrawer avatarUrl debug:', avatarUrl);
  const myId         = user?.id;

  // Group messages by date for dividers
  const grouped = [];
  let lastDate = '';
  for (const msg of messages) {
    const dl = dateLabel(msg.createdAt);
    if (dl !== lastDate) {
      grouped.push({ type: 'divider', label: dl });
      lastDate = dl;
    }
    grouped.push({ type: 'msg', msg });
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      zIndex: 1000,
      display: 'flex',
      alignItems: 'stretch',
      justifyContent: 'flex-end',
      pointerEvents: 'none',
    }}>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(4px)',
          pointerEvents: 'all',
        }}
      />

      {/* Drawer */}
      <div style={{
        position: 'relative',
        width: 400,
        maxWidth: '100vw',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRight: 'none',
        borderRadius: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        pointerEvents: 'all',
        boxShadow: '-8px 0 40px rgba(0,0,0,0.4)',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          paddingTop: 'calc(16px + var(--sat))',
          paddingBottom: '16px',
          paddingLeft: 'calc(20px + var(--sal))',
          paddingRight: 'calc(20px + var(--sar))',
          borderBottom: '1px solid var(--border)',
          background: 'var(--surface)',
          flexShrink: 0,
        }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <Avatar user={targetUser} size={40} />
            <div style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 10, height: 10, borderRadius: '50%',
              background: wsReady ? '#10b981' : 'var(--text3)',
              border: '2px solid var(--surface)',
            }} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {targetUser?.displayName || targetUser?.display_name || 'Member'}
            </div>
            <div style={{ fontSize: 11, color: wsReady ? '#10b981' : 'var(--text3)' }}>
              {wsReady ? 'Connected' : 'Connecting…'}
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              background: 'var(--surface2)', border: 'none', borderRadius: '50%',
              width: 32, height: 32, cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'var(--text2)', fontSize: 16, flexShrink: 0,
            }}
          >✕</button>
        </div>

        {/* Message list */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 16px 8px',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {loading && (
            <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: 32 }}>
              Loading messages…
            </div>
          )}

          {error && (
            <div style={{
              textAlign: 'center',
              fontSize: 13,
              padding: '40px 24px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}>
              <span style={{ fontSize: 36 }}>⚠️</span>
              <span style={{ fontWeight: 600, color: 'var(--text)', lineHeight: 1.5 }}>{error}</span>
              <button
                onClick={() => { setError(null); setConvId(null); }}
                style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
              >Try again</button>
            </div>
          )}

          {!loading && !error && grouped.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 13, padding: 32 }}>
              Start the conversation!
            </div>
          )}

          {grouped.map((item, i) => {
            if (item.type === 'divider') {
              return (
                <div key={`d-${i}`} style={{
                  textAlign: 'center', fontSize: 11, color: 'var(--text3)',
                  margin: '12px 0 8px', fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                  {item.label}
                  <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
                </div>
              );
            }

            const { msg } = item;
            const isMe = msg.senderId === myId;

            return (
              <div key={msg.id} style={{
                display: 'flex',
                flexDirection: isMe ? 'row-reverse' : 'row',
                gap: 8,
                alignItems: 'flex-end',
                marginBottom: 4,
                opacity: msg._optimistic ? 0.7 : 1,
              }}>
                {/* Avatar (only for other person) */}
                {!isMe && (
                  <Avatar user={{ avatar: msg.senderAvatar || avatarUrl, name: msg.senderName || targetUser?.displayName }} size={28} />
                )}

                <div style={{ maxWidth: '72%', display: 'flex', flexDirection: 'column', gap: 2, alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                  {/* Bubble */}
                  <div style={{
                    background: isMe ? 'var(--accent)' : 'var(--surface2)',
                    color: isMe ? '#fff' : 'var(--text)',
                    borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    padding: msg.content ? '10px 14px' : '6px',
                    fontSize: 14,
                    lineHeight: 1.45,
                    wordBreak: 'break-word',
                  }}>
                    {/* Text content */}
                    {msg.content && <div style={{ whiteSpace: 'pre-wrap' }}>{msg.content}</div>}

                    {/* Attachment */}
                    {msg.attachmentUrl && (
                      <AttachmentPreview
                        url={msg.attachmentUrl}
                        type={msg.attachmentType}
                        name={msg.attachmentName}
                        isMe={isMe}
                        onImageClick={setPreviewImage}
                      />
                    )}
                  </div>

                  {/* Timestamp */}
                  <div style={{ fontSize: 10, color: 'var(--text3)', paddingLeft: 4, paddingRight: 4 }}>
                    {timeStr(msg.createdAt)}
                    {isMe && <span style={{ marginLeft: 4 }}>{msg.isRead ? '✓✓' : '✓'}</span>}
                  </div>
                </div>
              </div>
            );
          })}

          <div ref={bottomRef} />
        </div>

        {/* Input area */}
        <div style={{
          borderTop: '1px solid var(--border)',
          paddingTop: '12px',
          paddingBottom: 'calc(12px + var(--sab))',
          paddingLeft: 'calc(16px + var(--sal))',
          paddingRight: 'calc(16px + var(--sar))',
          background: 'var(--surface)',
          flexShrink: 0,
        }}>
          <form onSubmit={sendText} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            {/* Attachment button */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z"
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading || !!error}
              title="Attach file"
              style={{
                background: 'var(--surface2)', border: '1px solid var(--border)',
                borderRadius: 12, width: 40, height: 40, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: 'var(--text2)', flexShrink: 0,
                opacity: (uploading || error) ? 0.3 : 1,
              }}
            >
              {uploading ? '⏳' : '📎'}
            </button>

            {/* Text input */}
            <textarea
              value={text}
              disabled={!!error}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendText(e);
                }
              }}
              placeholder={error ? "Chat features are locked." : "Type a message… (Enter to send)"}
              rows={1}
              style={{
                flex: 1,
                resize: 'none',
                border: '1px solid var(--border)',
                borderRadius: 12,
                padding: '10px 14px',
                background: error ? 'var(--bg)' : 'var(--surface2)',
                color: error ? 'var(--text3)' : 'var(--text)',
                fontSize: 14,
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: 1.4,
                maxHeight: 120,
                overflowY: 'auto',
              }}
            />

            {/* Send button */}
            <button
              type="submit"
              disabled={!text.trim() || !convId || !!error}
              style={{
                background: 'var(--accent)',
                border: 'none',
                borderRadius: 12,
                width: 40, height: 40,
                cursor: text.trim() && convId && !error ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: '#fff', flexShrink: 0,
                opacity: text.trim() && convId && !error ? 1 : 0.4,
                transition: 'opacity 0.2s',
              }}
            >
              ➤
            </button>
          </form>
        </div>
      </div>

      {/* ── Image Preview Modal ── */}
      {previewImage && (
        <div 
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
            cursor: 'zoom-out',
            pointerEvents: 'all'
          }}
          onClick={(e) => {
            e.stopPropagation();
            setPreviewImage(null);
          }}
        >
          <button 
            style={{
              position: 'absolute',
              top: 20,
              right: 20,
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              borderRadius: '50%',
              width: 44,
              height: 44,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s',
            }}
            onClick={(e) => {
              e.stopPropagation();
              setPreviewImage(null);
            }}
          >
            ✕
          </button>
          <img 
            src={previewImage} 
            alt="Preview" 
            style={{
              maxWidth: '90%',
              maxHeight: '90%',
              objectFit: 'contain',
              borderRadius: 8,
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)',
              cursor: 'default'
            }}
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

// ── Attachment preview ─────────────────────────────────────────────────────
function AttachmentPreview({ url, type, name, isMe, onImageClick }) {
  const fullUrl = `${API}${url}`;

  if (type === 'image') {
    return (
      <div 
        onClick={() => onImageClick(fullUrl)} 
        style={{ display: 'block', marginTop: 4, cursor: 'pointer' }}
      >
        <img
          src={fullUrl}
          alt={name || 'image'}
          style={{ maxWidth: 220, maxHeight: 200, borderRadius: 10, display: 'block', objectFit: 'cover' }}
        />
      </div>
    );
  }

  if (type === 'video') {
    return (
      <video
        src={fullUrl}
        controls
        style={{ maxWidth: 220, borderRadius: 10, marginTop: 4, display: 'block' }}
      />
    );
  }

  // Generic file
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      download={name}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 4,
        padding: '8px 12px',
        background: isMe ? 'rgba(255,255,255,0.15)' : 'var(--surface)',
        border: `1px solid ${isMe ? 'rgba(255,255,255,0.2)' : 'var(--border)'}`,
        borderRadius: 10,
        textDecoration: 'none',
        color: isMe ? '#fff' : 'var(--text)',
        fontSize: 13,
        maxWidth: 200,
      }}
    >
      <span style={{ fontSize: 20 }}>📎</span>
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {name || 'File'}
      </span>
      <span style={{ fontSize: 11, opacity: 0.7, flexShrink: 0 }}>↓</span>
    </a>
  );
}
